// Counter to ensure uniqueness even for same names created in same millisecond
let nameCounter = 0;

/**
 * Generates a unique identifier based on the organization name
 * @param name - The organization name
 * @param maxLength - Maximum length of the identifier (default: 6)
 * @returns A unique identifier string
 */
export const generateIdentifier = (
  name: string,
  maxLength: number = 6
): string => {
  if (!name) return "";

  // Remove special characters and convert to uppercase
  const cleanName = name.toUpperCase().replace(/[^A-Z0-9]/g, "");

  // Get current timestamp and increment counter
  const timestamp = Date.now();
  const counter = ++nameCounter;

  // For shorter identifiers, use a more compact unique suffix
  let uniqueSuffix: string;
  let maxNameLength: number;

  if (maxLength <= 6) {
    // For 6 characters or less, use a more compact approach
    // Use last 2-3 digits of timestamp + counter for uniqueness
    const shortTimestamp = (timestamp % 1000).toString(36).toUpperCase();
    const shortCounter = counter.toString(36).toUpperCase();
    uniqueSuffix = (shortTimestamp + shortCounter).substring(0, 2);
    maxNameLength = Math.max(1, maxLength - 2);
  } else {
    // For longer identifiers, use full timestamp + counter
    uniqueSuffix = (timestamp + counter).toString(36).toUpperCase();
    maxNameLength = Math.max(1, maxLength - 4);
  }

  const truncatedName = cleanName.substring(0, maxNameLength);

  // If the name is shorter than the available space, pad with random characters
  if (truncatedName.length < maxNameLength) {
    const randomChars = Math.random()
      .toString(36)
      .substring(2, 2 + maxNameLength - truncatedName.length)
      .toUpperCase();
    return (truncatedName + randomChars + uniqueSuffix).substring(0, maxLength);
  }

  // Combine name and unique suffix, then truncate to maxLength
  return (truncatedName + uniqueSuffix).substring(0, maxLength);
};

/**
 * Resets the name counter (useful for testing or starting fresh)
 */
export const resetNameCounter = (): void => {
  nameCounter = 0;
};

/**
 * Generates a unique identifier with timestamp to ensure uniqueness
 * @param name - The organization name
 * @param maxLength - Maximum length of the identifier (default: 8)
 * @returns A unique identifier string with timestamp
 */
export const generateUniqueIdentifier = (
  name: string,
  maxLength: number = 8
): string => {
  // Use the improved generateIdentifier function which now handles length constraints better
  return generateIdentifier(name, maxLength);
};
